import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { Upload, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '@/lib/supabase';
import { useAppContext } from '@/contexts/AppContext';
import { normalizePhoneInput } from '@/lib/phone';

interface BulkUploadProps {
  type: 'drivers' | 'vehicles';
}

const BulkUpload: React.FC<BulkUploadProps> = ({ type }) => {
  const { setDrivers, setFleetVehicles } = useAppContext();
  const [uploading, setUploading] = useState(false);

  const downloadTemplate = () => {
    let templateData: any[] = [];
    let fileName = '';
    
    if (type === 'drivers') {
      templateData = [{
        'Driver Name': 'John Smith',
        'Password': 'password123',
        'RideWise ID': 'RW001',
        'Email': 'john@example.com',
        'Phone': '+61400123456',
        'Admin Rights': 'No'
      }];
      fileName = 'drivers_template.xlsx';
    } else {
      templateData = [{
        'Fleet Number': 'CAB001',
        'Model': 'Toyota Camry',
        'Year': '2020',
        'EFT ID': 'EFT001'
      }];
      fileName = 'vehicles_template.xlsx';
    }

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, type === 'drivers' ? 'Drivers' : 'Vehicles');
    XLSX.writeFile(workbook, fileName);
    
    toast({ title: 'Success', description: `Template downloaded: ${fileName}` });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (type === 'drivers') {
        await uploadDrivers(jsonData);
      } else {
        await uploadVehicles(jsonData);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Error', description: 'Failed to process file', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const uploadDrivers = async (data: any[]) => {
    const drivers = data.map((row: any, index: number) => {
      const driverName = row['Driver Name']?.toString().trim();
      const password = row['Password']?.toString().trim();
      
      if (!driverName || !password) {
        throw new Error(`Row ${index + 1}: Driver Name and Password are required`);
      }

      return {
        driver_name: driverName,
        password: password,
        ridewise_id: row['RideWise ID']?.toString().trim() || null,
        email: row['Email']?.toString().trim() || null,
        phone: row['Phone'] ? normalizePhoneInput(row['Phone'].toString().trim()) || null : null,
        is_admin: row['Admin Rights']?.toString().toLowerCase() === 'yes'
      };
    });

    const { error } = await supabase
      .from('drivers')
      .upsert(drivers, { onConflict: 'driver_name' });

    if (error) throw error;

    // Update local state
    const localDrivers = drivers.map((d, i) => ({
      id: Date.now().toString() + i,
      name: d.driver_name,
      password: d.password,
      rideWiseId: d.ridewise_id || undefined,
      email: d.email || undefined,
      phone: d.phone || undefined,
      isAdmin: d.is_admin
    }));
    
    setDrivers(prev => [...prev, ...localDrivers]);
    toast({ title: 'Success', description: `${drivers.length} drivers uploaded successfully` });
  };

  const uploadVehicles = async (data: any[]) => {
    const vehicles = data.map((row: any, index: number) => {
      const fleetNumber = row['Fleet Number']?.toString().trim();
      const model = row['Model']?.toString().trim();
      const year = row['Year']?.toString().trim();
      
      if (!fleetNumber || !model || !year) {
        throw new Error(`Row ${index + 1}: Fleet Number, Model, and Year are required`);
      }

      return {
        vehicle_number: fleetNumber,
        model: model,
        year: year,
        eft_id: row['EFT ID']?.toString().trim() || null
      };
    });

    const { error } = await supabase
      .from('vehicles')
      .upsert(vehicles, { onConflict: 'vehicle_number' });

    if (error) throw error;

    // Update local state
    const localVehicles = vehicles.map((v, i) => ({
      id: Date.now().toString() + i,
      fleetNumber: v.vehicle_number,
      model: v.model,
      year: v.year,
      eftId: v.eft_id || undefined
    }));
    
    setFleetVehicles(prev => [...prev, ...localVehicles]);
    toast({ title: 'Success', description: `${vehicles.length} vehicles uploaded successfully` });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulk Upload {type === 'drivers' ? 'Drivers' : 'Vehicles'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Step 1: Download Template</Label>
          <Button onClick={downloadTemplate} variant="outline" className="w-full">
            <Download className="w-4 h-4 mr-2" />
            Download {type === 'drivers' ? 'Drivers' : 'Vehicles'} Template
          </Button>
        </div>
        
        <div className="space-y-2">
          <Label>Step 2: Upload Completed File</Label>
          <div className="flex items-center space-x-2">
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <Button disabled={uploading}>
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          <p><strong>Instructions:</strong></p>
          <ul className="list-disc list-inside space-y-1">
            <li>Download the template file first</li>
            <li>Fill in your data following the exact column format</li>
            <li>Save as Excel file (.xlsx)</li>
            <li>Upload the completed file</li>
            {type === 'drivers' && (
              <li>Admin Rights: Enter "Yes" or "No"</li>
            )}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default BulkUpload;